import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Masks, C, T } from 'shared/helpers';

import { Creators as reduxPerguntas } from 'store/ducks/perguntas';
import { Creators as reduxRespostas } from 'store/ducks/respostas';
import { Creators as reduxPems } from 'store/ducks/pems';

import Styles from './DadosIniciais.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListOl, faEdit } from '@fortawesome/free-solid-svg-icons';

import Header from 'components/Header/Header';
import Table from './Table/Table';
import { Multiselect } from 'components/Template';
import NumberFormat from 'react-number-format';

import { useEditableContext } from 'shared/hooks';
import { ONLY_NUMBER } from 'utils/regex';
import PrintButton from 'components/PrintButton/PrintButton';
import WithPermission from 'components/Template/WithPermission/WithPermission';

export default function DadosIniciais({ match }) {
  const dispatch = useDispatch();
  const {
    DadosIniciais,
    TextField,
    SelectContainer,
    Selected,
    TextArea,
    Saver,
    Hidden,
  } = Styles;

  const block = useRef();

  // PEM ID
  const { id } = match.params;
  // Editing form toggle
  const [edit, toggleEdit] = useState(false);
  // Form state (which is sent to API)
  const [form, setForm] = useState({});

  /** In order to write details like Cultura */
  const PEM = useSelector((state) =>
    state.pems.data.find((pem) => String(pem.id) === String(id)),
  );
  /* Select all respostas in reducer */
  const Perguntas = useSelector((state) => state.perguntas.data);
  const Respostas = useSelector((state) => state.respostas.data);
  /* Filter those appropriate to use in this screen */
  const perguntas = T(Perguntas, []).filter(
    (pergunta) => pergunta.fase.id === 1,
  );
  const respostas = T(Respostas, []).filter(
    (resposta) =>
      !!Perguntas.find((pergunta) => pergunta.id === resposta.pergunta_id),
  );

  useEditableContext(edit);

  /** The following 10 lines were called many times in the code below
   *  so I standardised them here. This function simply filters
   *  the already-filtered-by-step respostas by a condition, usually
   *  to match a resposta to a specific pergunta.
   */
  const getRespostas = (
    matches,
    modifier = (ans) => ans,
    respostas = [],
    onFailure = '',
  ) => {
    try {
      const resposta =
        (
          [...respostas].find(matches) || {
            resposta: modifier === JSON.parse ? '[]' : '',
          }
        ).resposta || '';
      return Array.isArray(resposta) ? resposta : modifier(resposta);
    } catch (oof) {
      return onFailure;
    }
  };

  /** Update Perguntas/Respostas stored in the Redux store
   *  every time the currently-selected PEM changes.
   *  Based on the PEM's ID taken out of the browser URL,
   *  as basing it on the Redux store could lead to instability.
   */
  useEffect(() => {
    dispatch(reduxPems.getPems());
    dispatch(reduxPerguntas.getPerguntas((pergunta) => pergunta.fase.id === 1));
    dispatch(
      reduxRespostas.getRespostas(
        (resposta) => String(resposta.pem_id) === String(id),
      ),
    );
  }, [id, dispatch]);

  useEffect(() => {
    /* Might look like redeclaring, but by instantiating those here,
       the effect is now independent from the redux selectors, saving several renders. */
    const perguntas = T(Perguntas, []).filter(
      (pergunta) => pergunta.fase.id === 1,
    );
    const respostas = T(Respostas, []).filter(
      (resposta) =>
        !!Perguntas.find((pergunta) => pergunta.id === resposta.pergunta_id),
    );

    perguntas.map((pergunta) =>
      setForm((current) => ({
        ...current,
        [pergunta.id]: getRespostas(
          (x) => String(x.pergunta_id) === String(pergunta.id),
          pergunta.attributes.class.includes('multiselect')
            ? JSON.parse
            : undefined,
          respostas,
        ),
      })),
    );
  }, [Perguntas, Respostas, edit]);

  /** Standardised function that deals with
   *  the cumbersome ways components tend to write to the state. */
  const editForm = ({ formattedValue, target }, index) => {
    const { name } = target || { value: '', name: '' };
    if (!formattedValue) {
      setForm((current) => ({
        ...current,
        [name]: (name && (target || { value: '' }).value) || null,
      }));
    } else {
      setForm((current) => ({
        ...current,
        [index]: (index && (formattedValue || '')) || null,
      }));
    }
  };

  /** Generates the rows fillers for the table the below JSX.
   *  Varies mostly by the edit state (text fields vs simple table).
   *  Expects number data to be NUMBERS, as it will deal with formatting
   *  the data itself.
   */
  const rows = !edit
    ? perguntas.map(({ id, pergunta, attributes }) => [
        // eslint-disable-next-line react/jsx-indent
        <p style={{ textIndent: '0', padding: '0 10px' }}>{pergunta}</p>,
        // eslint-disable-next-line react/jsx-indent
        <p style={{ textIndent: '0', padding: '0 10px' }}>
          {attributes &&
          attributes.class &&
          attributes.class.includes('reaisSoNumeros')
            ? `${getRespostas(
                (x) => String(x.pergunta_id) === String(id),
                (x) =>
                  Masks.number(
                    x
                      .replace(ONLY_NUMBER, '')
                      .replace(/\./gi, '')
                      .replace(',', '.'),
                  ),
                respostas,
              )} ${getRespostas(
                (x) => String(x.pergunta_id) === '2',
                undefined,
                respostas,
              )}/ha`
            : attributes.class.includes('multiselect')
            ? getRespostas(
                (x) => String(x.pergunta_id) === String(id),
                JSON.parse,
                respostas,
                [],
              ).join(', ')
            : attributes.class.includes('hectare')
            ? `${getRespostas(
                (x) => String(x.pergunta_id) === String(id),
                (x) =>
                  Masks.number(
                    x.replace(' /ha', '').replace(/\./gi, '').replace(',', '.'),
                  ),
                respostas,
              )} /ha`
            : attributes.class.includes('tonelada')
            ? `${getRespostas(
                (x) => String(x.pergunta_id) === String(id),
                (x) =>
                  Masks.number(
                    x
                      .replace(' /ton', '')
                      .replace(/\./gi, '')
                      .replace(',', '.'),
                  ),
                respostas,
              )} /ton`
            : getRespostas(
                (x) => String(x.pergunta_id) === String(id),
                undefined,
                respostas,
              )}
        </p>,
      ])
    : perguntas.map(({ id, type, pergunta, options, attributes }) => {
        const mask =
          attributes.class && attributes.class
            ? attributes.class.split(' ')[2]
            : '';

        const res = [<p>{pergunta}</p>];
        switch (type) {
          case 'select':
            res.push(
              <div className={SelectContainer}>
                <Multiselect
                  style={{
                    borderRadius: '5px',
                    fontSize: '1.5em',
                    width: '130%',
                    boxShadow: 'none',
                    margin: '-10px 0px',
                  }}
                  selectKey={pergunta}
                  label={null}
                  stateKey={id}
                  valueRef={form[id] instanceof Array ? form[id] : []}
                  setValueRef={setForm}
                  options={options.map(({ label, value, resposta }) =>
                    value && label ? { label, value } : resposta || '',
                  )}
                />
              </div>,
            );
            break;
          case 'textarea':
            res.push(
              <textarea
                style={{ minHeight: '80px', minWidth: '88%' }}
                className={C(TextField, TextArea)}
                type='text'
                name={id}
                onChange={editForm}
                value={form[id] || ''}
              />,
            );
            break;
          default:
            switch (mask) {
              case 'hectare':
                res.push(
                  <NumberFormat
                    allowEmptyFormatting
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    suffix={' /ha'}
                    value={form[id] || ''}
                    onValueChange={(value) => editForm(value, id)}
                    className={TextField}
                    type='text'
                    name={id}
                  />,
                );
                break;
              case 'tonelada':
                res.push(
                  <NumberFormat
                    allowEmptyFormatting
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    suffix={' /ton'}
                    value={form[id] || ''}
                    onValueChange={(value) => editForm(value, id)}
                    className={TextField}
                    type='text'
                    name={id}
                  />,
                );
                break;
              case 'reaisSoNumeros':
                res.push(
                  <NumberFormat
                    allowEmptyFormatting
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    suffix={` ${form[2] || ''}/ha`}
                    value={form[id] || ''}
                    onValueChange={(value) =>
                      editForm(
                        { formattedValue: value.value, target: { name: id } },
                        id,
                      )
                    }
                    className={TextField}
                    type='text'
                    name={id}
                  />,
                );
                break;
              default:
                res.push(
                  <input
                    className={TextField}
                    type='text'
                    name={id}
                    onChange={editForm}
                    value={form[id] || ''}
                  />,
                );
            }
        }
        return res;
      });

  /** Simple edit toggle so that JSX looks simpler. */
  const edite = () => toggleEdit(!edit);

  /** Send handler so that JSX looks simpler. */
  const send = () => {
    if (block.current) return;
    block.current = true;
    dispatch(
      reduxRespostas.saveRespostas(
        id,
        1,
        { pergunta: form },
        (resposta) => String(resposta.pem_id) === String(id),
      ),
    );
    toggleEdit(false);
  };

  useEffect(() => {
    if (edit) {
      block.current = false;
    }
  }, [edit]);

  return (
    <div className={DadosIniciais}>
      <Header icon={faListOl} title={'QUESTIONÁRIO GERAL E ANOTAÇÕES DIVERSAS'}>
        <WithPermission permission='respostas.create'>
          <button id='editar' className={C([edit, Selected])} onClick={edite}>
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </WithPermission>
        <PrintButton />
      </Header>
      <button
        onClick={send}
        className={C(Saver, [!edit, Hidden])}
        id='btn_salvar_respostas'
      >
        Salvar
      </button>
      <main>
        {PEM && (
          <Table
            headers={['CULTURA', PEM.cultura.nome]}
            rows={rows}
            tableKey={'dadosIniciais'}
          />
        )}
      </main>
    </div>
  );
}

DadosIniciais.propTypes = {
  match: PropTypes.any.isRequired,
};
